import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import * as customPropTypes from '@/custom-prop-types';

import { useSiteSearchContext } from '../../contexts/SiteSearchContext';
import { SearchRecommendedCard } from '../SearchRecommendedCard';
import styles from './SearchRecommendedResultsList.module.scss';

const MAX_RECOMMENDED_RESULTS = 2;

/**
 * Some filters may have fixed recommendations.
 * @param {string} activeFilter - The active filter
 * @param {object} fixedRecommendedCards - The fixed recommended cards
 * @param {object} analytics - The analytics object
 * @returns {React.Component | null} The fixed recommendation card
 */
const getFixedRecommendation = (activeFilter, fixedRecommendedCards, analytics) => {
    const fixedRecommendation = fixedRecommendedCards?.[activeFilter] || null;
    if (!fixedRecommendation) return null;

    return <SearchRecommendedCard result={fixedRecommendation} analytics={analytics} />;
};

/**
 * The SearchRecommendedResultsList component displays recommended search results.
 * @param {object} props - The component props
 * @param {string} props.activeFilter - The active filter
 * @param {string} props.className - Additional class name
 * @param {array} props.results - The recommended search results
 * @returns {React.Component} The recommended search results component
 */
const SearchRecommendedResultsList = ({
    activeFilter,
    className,
    results,
    analytics,
    ...props
}) => {
    const { fixedRecommendedCards } = useSiteSearchContext();
    const fixedRecommendationCard = getFixedRecommendation(
        activeFilter,
        fixedRecommendedCards,
        analytics,
    );

    // If there are no recommended results or fixed results card, return null
    if (!fixedRecommendationCard && results.length === 0) return null;

    return (
        <ul className={classnames(styles['search-recommended-results'], className)} {...props}>
            {/* Display a fixed recommendation card for specific filters */}
            {fixedRecommendationCard ? (
                <li className={styles['search-recommended-results__item']}>
                    {fixedRecommendationCard}
                </li>
            ) : null}

            {results
                ?.slice(
                    0,
                    fixedRecommendationCard ? MAX_RECOMMENDED_RESULTS - 1 : MAX_RECOMMENDED_RESULTS,
                )
                .map((result, index) => {
                    const resultObject = result;
                    delete resultObject.isRecommendation;

                    return (
                        <li className={styles['search-recommended-results__item']} key={index}>
                            <SearchRecommendedCard
                                key={index}
                                result={resultObject}
                                analytics={analytics}
                            />
                        </li>
                    );
                })}
        </ul>
    );
};

SearchRecommendedResultsList.propTypes = {
    className: PropTypes.string,
    results: PropTypes.array,
    analytics: customPropTypes.analyticsPropType,
    activeFilter: PropTypes.string,
};

SearchRecommendedResultsList.defaultProps = {
    results: [],
    activeFilter: null,
};

export default SearchRecommendedResultsList;
